/**
 * Helpers for the in-run play log (domain, Phaser-free).
 * DungeonRunState.playLog is the persistence source; encounter.log feeds into it.
 */

/** Cap recent history; oldest lines drop when exceeded. */
export const MAX_PLAY_LOG_LINES = 80;

/** Newest lines last; returns up to `maxLines` entries for the UI. */
export function getRecentCombatLog(
  log: readonly string[],
  maxLines = 8,
): string[] {
  if (maxLines <= 0) {
    return [];
  }
  if (log.length <= maxLines) {
    return [...log];
  }
  return log.slice(log.length - maxLines);
}

/** Append lines and trim to MAX_PLAY_LOG_LINES (drop oldest). */
export function appendPlayLog(
  log: readonly string[],
  lines: readonly string[],
): string[] {
  if (lines.length === 0) {
    return [...log];
  }
  const next = [...log, ...lines];
  if (next.length <= MAX_PLAY_LOG_LINES) {
    return next;
  }
  return next.slice(next.length - MAX_PLAY_LOG_LINES);
}

/**
 * Absorbs new encounter.log entries into the run play log.
 * `cursor` is how many encounter.log lines were already merged.
 */
export function absorbEncounterIntoPlayLog(
  playLog: readonly string[],
  cursor: number,
  encounterLog: readonly string[],
): { playLog: string[]; cursor: number } {
  const safeCursor = Math.max(0, Math.min(cursor, encounterLog.length));
  const fresh = encounterLog.slice(safeCursor);
  return {
    playLog: appendPlayLog(playLog, fresh),
    cursor: encounterLog.length,
  };
}

/** Formats the play-log panel body (newest at bottom). */
export function formatCombatLogPanel(
  log: readonly string[],
  maxLines = 8,
): string {
  const recent = getRecentCombatLog(log, maxLines);
  if (recent.length === 0) {
    return 'Play log is empty…';
  }
  return recent.join('\n');
}

export function formatPlayerAttackLog(args: {
  comboName: string;
  targetName: string;
  damageDealt: number;
  faceSum: number;
  multiplier: number;
  targetHpAfter: number;
  targetMaxHp: number;
  guardBlocked?: number;
}): string {
  const multLabel =
    args.multiplier === 1
      ? `faces ${args.faceSum}`
      : `faces ${args.faceSum}×${args.multiplier}`;
  const guard =
    args.guardBlocked && args.guardBlocked > 0
      ? ` · guard −${args.guardBlocked}`
      : '';
  return `${args.comboName} → ${args.targetName}: ${args.damageDealt} dmg (${multLabel}${guard}) · HP ${args.targetHpAfter}/${args.targetMaxHp}`;
}

export function formatRewardLog(args: {
  gold: number;
  materials: number;
  bossChest: boolean;
  isBoss: boolean;
}): string {
  const bits = [`+${args.gold} gold`, `+${args.materials} materials`];
  if (args.bossChest) {
    bits.push('boss chest (placeholder)');
  }
  return `${args.isBoss ? 'Boss reward' : 'Reward'}: ${bits.join(', ')}`;
}

export function formatEncounterBanner(
  encounterIndex: number,
  encounterCount: number,
  isBoss?: boolean,
): string {
  const n = encounterIndex + 1;
  return isBoss
    ? `--- Encounter ${n}/${encounterCount} · BOSS ---`
    : `--- Encounter ${n}/${encounterCount} ---`;
}
